/* Copyright (c) 2019 - 2021. Verimatrix. All Rights Reserved.
   All information in this file is Verimatrix Confidential and Proprietary. */

import React, { useState } from "react";
import { Auth } from "@aws-amplify/auth";
import { CognitoUser } from "amazon-cognito-identity-js";
import { FormProps } from "aws-amplify-react/props";
import { Form, Button } from "semantic-ui-react";
import { notify, passwordValidator } from "./utils";
import styles from "./LandingPage/LandingForm/LandingForm.module.css";
import cStyles from "../../components/Components.module.css";
import useForm from "react-hook-form";
import { fieldContainer } from "./FieldContainer";
import { useTranslation } from "react-i18next";

type FormData = {
  password: string;
};

const RequireNewPassword: React.FC<FormProps> = ({
  authData,
  authState = "",
  onStateChange,
  onAuthEvent,
  setLoading,
  loading,
  setMessage,
  hide = [],
}) => {
  const { register, handleSubmit, errors, getValues } = useForm<FormData>();
  const { changeState } = notify(authState, onAuthEvent, onStateChange);
  const { t } = useTranslation("common");

  const [showPassword, setShowPassword] = useState(false);

  const validAuthStates = ["requireNewPassword"];
  if (!validAuthStates.includes(authState)) {
    return null;
  }

  const checkContact = (user: CognitoUser) => {
    if (!Auth || typeof Auth.verifiedContact !== "function") {
      throw new Error(
        "No Auth module found, please ensure @aws-amplify/auth is imported"
      );
    }

    Auth.verifiedContact(user).then((data) => {
      if (!!Object.keys(data.verified).length) {
        changeState("signedIn", user);
      } else {
        user = Object.assign(user, data);
        changeState("verifyContact", user);
      }
    });
  };

  const setNewPassword = (data: FormData) => {
    if (!Auth || typeof Auth.completeNewPassword !== "function") {
      throw new Error(
        "No Auth module found, please ensure @aws-amplify/auth is imported"
      );
    }

    setMessage(null);
    setLoading(true);
    const { password } = data;

    Auth.completeNewPassword(authData, password, {})
      .then((user) => {
        if (
          user.challengeName === "SMS_MFA" ||
          user.challengeName === "SOFTWARE_TOKEN_MFA"
        ) {
          changeState("confirmSignIn", user);
        } else if (user.challengeName === "MFA_SETUP") {
          changeState("TOTPSetup", user);
        } else {
          checkContact(user);
        }
      })
      .catch((err) => setMessage(err.message || err))
      .finally(() => {
        setLoading(false);
      });
  };

  if (hide && hide.includes(RequireNewPassword)) {
    return null;
  }

  return (
    <React.Fragment>
      <Form
        onSubmit={handleSubmit(setNewPassword)}
        className={styles.signupForm}
      >
        <div className={styles.formHint}>{t("new_password_for_account")}</div>
        <Form.Group inline className={styles.formGroup}>
          <Form.Field
            required
            error={!!errors.password}
            className={styles.formField}
          >
            {fieldContainer(
              {
                label: t("password"),
                errors,
                actionIcon: showPassword ? "eye slash" : "eye",
                actionIconAction: () => {
                  setShowPassword(!showPassword);
                },
              },
              {
                autoFocus: true,
                name: "password",
                key: "password",
                type: showPassword ? "text" : "password",
                placeholder: t("enter_new_password"),
                autoComplete: "off",
                ref: register({
                  required: t("common:please_enter_password") as string,
                  pattern: {
                    value: passwordValidator,
                    message: "",
                  },
                }),
              }
            )}
          </Form.Field>
        </Form.Group>

        <div
          className={
            errors?.password?.type === "pattern"
              ? styles.fieldAlwaysVisibleError
              : styles.fieldAlwaysVisible
          }
        >
          {t("valid_password")}
        </div>

        <Form.Group inline className={styles.formGroup}>
          <Form.Field
            required
            error={!!errors.confirmPassword}
            className={styles.formField}
          >
            {fieldContainer(
              { label: t("confirm"), errors },
              {
                name: "confirmPassword",
                key: "confirmPassword",
                type: "password",
                placeholder: t("confirm_password"),
                autoComplete: "off",
                ref: register({
                  validate: (value) =>
                    value === getValues().password ||
                    (t("password_no_match") as string),
                }),
              }
            )}
          </Form.Field>
        </Form.Group>
        <Button
          className={`${cStyles.lpButton} ${styles.submitFormButton}`}
          id="setPasswordButton"
          loading={loading}
          disabled={loading}
          onClick={() => {
            setMessage(null);
          }}
        >
          {t("set_password")}
        </Button>
      </Form>
    </React.Fragment>
  );
};

export default RequireNewPassword;
